import type { LearningContentLanguage } from "../learning/types";

/** SC-17 — Home, Seat, Groups, nav, profile chrome (Learn strings stay in learn.ts). */
export type PortalLocale = LearningContentLanguage;

const SHELL_EN = {
  navHome: "Home",
  navSeat: "Seat",
  navLearn: "Learn",
  navGroups: "Groups",
  navSupport: "Support",
  navProfile: "Profile",
  greetingMorning: "Good morning",
  greetingAfternoon: "Good afternoon",
  greetingEvening: "Good evening",
  heroTagline: "Focus is your superpower today.",
  heroCta: "Let's get started",
  viewAll: "View all",
  todayAtAGlance: "Today at a glance",
  weekActivityTitle: "Last 7 days",
  weekActivityEmpty: "No study activity in the last 7 days.",
  weekActivityUnavailable: "Couldn't load your recent activity.",
  weekActivityLatestOnly: "Based on the latest activities on record.",
  topicsDueToday: (n: number) => `${n} topic${n === 1 ? "" : "s"} due today`,
  caughtUpToday: "Caught up today",
  dueNow: "Due now",
  reviewedToday: "Reviewed today",
  inRotation: "In rotation",
  yourSeat: "Your Seat",
  regular: "Regular",
  checkingSeat: "Checking your seat…",
  oneMoment: "One moment.",
  seatHeldToday: "Seat held for today",
  seatAssigned: "Seat assigned",
  noSeatYet: "No seat shared with you yet",
  sitAnywhere: "Sit anywhere",
  sitAnywhereHint: "No fixed desk. Check in on the Seat tab when you arrive.",
  noBookingYet: "No seat booked yet",
  bookSeatHint: "Pick a date and shift on the Seat tab to reserve a desk.",
  bookedAhead: "Seat booked",
  nextBooking: (day: string) => `Next: ${day}`,
  checkedIn: "Checked in",
  notCheckedInYet: "Not checked in yet",
  dedicatedSeatNoToday: "Your dedicated seat. Nothing held for today.",
  seatAssignHint: "Your centre will set up your seat. It shows up here once it's ready.",
  continueLearning: "Continue learning",
  homeLoadFailedTitle: "Couldn't load your dashboard",
  homeLoadFailedBody:
    "Something went wrong fetching your study data. Pull to refresh or try again in a moment.",
  studyStatsUnavailableTitle: "Study stats unavailable",
  studyStatsUnavailableBody:
    "We couldn't load today's review totals. Your learning progress is still safe.",
  homeRetry: "Try again",
  // Notices tab
  noticesTitle: "Notices",
  noticesLoading: "Loading notices…",
  noticesLoadFailedTitle: "Couldn't load notices",
  noticesLoadFailedBody: "Something went wrong fetching your updates. Try again in a moment.",
  noticesEmpty: "No notices yet — progress reports and centre updates will show up here.",
  noticesUnread: (n: number) => `${n} unread`,
  noticesMarkAllRead: "Mark all read",
  noticesMarkingAllRead: "Marking…",
  noticesMarkAllFailed: "Couldn't mark those as read. Try again in a moment.",
  noticesUnreadDot: "Unread",
  noticesOpen: (title: string) => `${title} — mark as read`,
  noticesListAria: "Notices",
  moduleDisabledHomeTitle: "Learning app not enabled",
  moduleDisabledHomeBody:
    "Your study centre hasn't turned on the learning app yet. Ask your centre admin.",
  setupFirstPlan: "Set up your first plan",
  setupFirstPlanHint: "Tell Ziggy what you're studying and why. Two minutes in Learn.",
  openLearn: "Open Learn",
  quickActions: "Quick actions",
  takeQuiz: "Take a quiz",
  practiceTest: "Practice test",
  logReading: "Log reading",
  progress: "Progress",
  progressReport: "Progress report",
  progressReportHint: "See your performance",
  newReportsBadge: (n: number) => `${n} new update${n === 1 ? "" : "s"}`,
  practiceTestHint: "Attempt mock tests",
  logReadingHint: "Keep track of your reads",
  takeQuizHint: "Find your weak topics",
  seatToday: "Today",
  nothingHeldToday: "Nothing held for today",
  nothingHeldHintAssigned: (label: string) =>
    `Your regular seat is ${label}. The desk can hold it for a shift.`,
  nothingHeldHintUnassigned: "Ask the front desk to book you a seat and it will show up here.",
  yourRegularSeat: "Your regular seat",
  seatDetailsAtDesk: "Seat details at the desk",
  zoneAtCentre: "Your centre",
  comingUp: "Coming up",
  attendance: "Attendance",
  attendanceToday: "Today",
  attendanceNoneToday: "No attendance recorded for today yet. Check in when you arrive.",
  last30Days: "Last 30 days",
  daysMarked: "Days marked",
  minLate: (n: number) => `${n} min late`,
  imHereCheckIn: "I'm here — check in",
  checkInFailed: "Couldn't check you in. Try again, or ask at the desk.",
  cancelBooking: "Cancel",
  cancelTodayBooking: "Cancel today's booking",
  cancelBookingOn: (day: string) => `Cancel booking on ${day}`,
  cancelBookingPrompt: "Cancel this booking? The seat opens up for others.",
  keepBooking: "Keep it",
  confirmCancelBooking: "Yes, cancel",
  cancelBookingFailed: "Couldn't cancel. Try again, or ask at the desk.",
  bookAnotherSeat: "Book another seat",
  notLinkedTitle: "Not linked to your centre's roster yet",
  seatUnavailableTitle: "Seat details unavailable",
  notLinkedBody:
    "Your centre needs to connect this login to your record before seats show up here. The front desk can do it in a minute.",
  seatLoadFailedBody: "We couldn't load your seat just now. Try again in a moment.",
  whatAppearsHere: "What appears here",
  explainSeatTitle: "Your seat & zone",
  explainSeatBody: "The exact seat the centre has set aside for you.",
  explainShiftTitle: "Your shift",
  explainShiftBody: "The hours your seat is held for, day by day.",
  explainDeskTitle: "Ask at the desk",
  explainDeskBody: "Until then, your centre's front desk has your seat details.",
  assignedByCentre: "assigned by your centre",
  inZone: (seat: string, zone: string) => `${seat} in ${zone}`,
  nothingHeldHintBookBelow: "Pick a date and shift below to reserve a desk.",
  bookASeat: "Book a seat",
  pickDate: "Date",
  pickShift: "Shift",
  pickZone: "Zone",
  pickZoneOptional: "Filter by zone",
  allZones: "All zones",
  slotMorning: "Morning",
  slotAfternoon: "Afternoon",
  slotEvening: "Evening",
  slotNight: "Night",
  slotFullDay: "Full day",
  attendanceStat: (value: string, label: string) => `${value} — ${label}`,
  bookSeat: "Book this seat",
  bookZone: "Book this zone",
  selected: "Selected",
  seatTaken: "Taken",
  seatAvailable: "Available",
  yourSeatLabel: "Your seat",
  loadingAvailability: "Checking what's free…",
  availabilityLoadFailed: "Couldn't load availability. Try again in a moment.",
  shiftsLoadFailed: "Couldn't load shifts. Try again in a moment.",
  noShiftsOnDay: "No shifts open on this day. Pick another date.",
  noZonesAvailable: "No zones open for this shift.",
  zoneSpotsLeft: (n: number, cap: number) => `${n} of ${cap} spots left`,
  zoneFullNoWaitlist: "This zone is full for the shift you picked.",
  zoneFullWaitlist: "Full — ask at the front desk to join the waitlist.",
  bookingConfirmedTitle: "You're booked",
  bookingConfirmedBody: (day: string, slot: string) => `${day} · ${slot}. See you then.`,
  bookingSeatPending: "Booking this seat…",
  bookingZonePending: "Booking this zone…",
  bookingFailedGeneric: "Couldn't complete the booking. Try again or ask at the desk.",
  seatAlreadyTaken: "That seat was just taken. Pick another.",
  alreadyBookedThisDay: "You already have a booking that day. Switching seats isn't enabled.",
  bookingNotAllowed: "Your centre doesn't allow this kind of booking from the app.",
  alreadyHavePermanentSeat: "You already have a permanent seat assigned.",
  namedShift: "A shift",
  bookByTheHour: "By the hour",
  customStart: "Start",
  customHours: "Hours",
  customCharge: (amount: string) => `Charge ${amount}`,
  customOverlaps: "That seat is already taken for those hours. Pick another seat or time.",
  customDisabled: "This centre isn't taking custom-length bookings right now.",
  permanentRoomsNote: "Permanent rooms are assigned at the desk and can't be booked here.",
  fixedRoomNotBookable: "This room uses permanent seats. Ask at the desk.",
  planTitle: "Your seat plan",
  planFixedSeat: (place: string) => `Your seat: ${place}. It's held for you every day.`,
  planFirstCome: "Book any free seat for a shift.",
  planFixedHoursShifts: (shifts: string) => `Your hours: ${shifts}. Book a seat inside them.`,
  planFixedHoursWindow: (start: string, end: string) =>
    `Your hours: ${start} – ${end} every day. Book a seat inside them.`,
  planFlexibleHours: "Pick a start time and how many hours you need.",
  planZoneOnly: (zone: string) => `Your room: ${zone}. Sit anywhere inside it.`,
  planNoSeat: "Sit anywhere — check in when you arrive",
  planValidUntil: (day: string) => `Until ${day}`,
  walkInCheckIn: "I'm here — check in",
  walkInCheckedIn: "Checked in for today",
  walkInCheckInFailed: "Couldn't check you in. Try again, or ask at the desk.",
  outsidePlanHours: "That time is outside your hours. Pick a time inside them.",
  zoneCapacityToday: (taken: number, cap: number) => `${taken} of ${cap} seats taken right now`,
  floorMapTitle: "Floor map",
  floorMapZoomIn: "Zoom in",
  floorMapZoomOut: "Zoom out",
  floorMapReset: "Fit floor",
  floorMapHint: "Pinch or use the buttons to zoom. Tap a free seat to pick it.",
  floorMapLive: "Live",
  floorMapPolling: "Updating every 15 seconds",
  floorSeatFree: "free",
  floorSeatTaken: "taken",
  floorSeatYours: "yours",
  floorSeatSelected: "selected",
  floorSeatAria: (seat: string, state: string) => `Seat ${seat}, ${state}`,
  floorMapAria: (name: string, free: number, total: number) =>
    `${name}: ${free} of ${total} seats free`,
  claimYourSeat: "Pick your desk",
  claimYourSeatHint: "Choose a permanent seat once. After that, the centre holds it for you.",
  claimSeat: "Claim this desk",
  claimFailed: "Couldn't claim that desk. Try another or ask at the front desk.",
  groupsTitle: "Study groups",
  newGroup: "New group",
  expandGroupList: "Expand group list",
  collapseGroupList: "Collapse group list",
  groupsLoadFailedTitle: "Couldn't load study groups",
  groupsLoadFailedBody: "Something went wrong fetching your groups. Try again in a moment.",
  noGroupsYet: "No study groups yet",
  noGroupsHint: "Start one to revise with peers at your centre.",
  archivedGroups: "Archived",
  archivedReadOnly:
    "Archived. You can read the history. New messages are closed until the centre restores it.",
  members: (count: number, max: number) => `${count}/${max} members`,
  chat: "Chat",
  chatWithGroup: (name: string) => `Open chat — ${name}`,
  join: "Join",
  joinGroupNamed: (name: string) => `Join ${name}`,
  createGroupTitle: "Create a study group",
  createGroupBlurb: "Peers at your centre can discover and join this group.",
  groupName: "Group name",
  descriptionOptional: "Description (optional)",
  createGroup: "Create group",
  needPlanFirst: "Create a study plan in Learn before starting a group.",
  groupNameMin: "Group name must be at least 3 characters.",
  inviteJoinFailed: "Could not join from invite link.",
  createGroupFailed: "Could not create the group.",
  joinGroupFailed: "Could not join the group.",
  selectGroupChat: "Select a group to open chat",
  selectGroupChatHint: "Pick a group from the list or create one to study together.",
  appLanguage: "App language",
  appLanguageHint:
    "Applies to portal labels and your quiz language preference. Syncs across devices.",
  languageDeviceOnly: "Saved on this device. It'll sync once your centre links your roster.",
  languageSaveFailed: "Couldn't save your language choice. Try again in a moment.",
  // Shared last-line error copy — see lib/apiErrorCopy.ts
  errorOffline: "Couldn't reach the server. Check your connection and try again.",
  errorTooManyRequests: "That's a lot of tries — wait a moment and go again.",
  errorServiceDown: "This isn't available right now. Try again shortly.",
  learnLanguage: "Learn language",
  learnLanguageHint:
    "Applies to Learn tab labels and your quiz language preference on this device.",
  appPaywallTitle: "Learner app",
  appPaywallBody: (centre: string) =>
    `${centre} offers the learning app as a separate subscription.`,
  appPaywallLapsedTitle: "Subscription expired",
  appPaywallLapsedBody: (centre: string, expiredOn: string) =>
    expiredOn
      ? `Your learner app access at ${centre} ended on ${expiredOn}. Renew to continue studying.`
      : `Your learner app access at ${centre} has expired. Renew to continue studying.`,
  appPaywallPayNow: "Pay now",
  appPaywallPaying: "Opening checkout…",
  appPaywallPayAtCentre:
    "Pay at the centre front desk, then ask the centre team to turn on your app access. Once granted, your study plans, coach and progress tools unlock here.",
  appPaywallPayAtCentreSecondary:
    "Prefer cash? Pay at the front desk and ask the centre team to turn on access — same result.",
  appPaywallPaymentFailed: "Payment could not be completed. Try again or pay at the centre.",
  appPaywallVerifyFailed:
    "We could not verify that payment. Contact the centre if money was debited.",
  appPaywallRenewalActive: (date: string) => `Access active — renews through ${date}.`,
  appPaywallLapsedDaysAgo: (days: number) =>
    days <= 0 ? "Expired today." : days === 1 ? "Expired 1 day ago." : `Expired ${days} days ago.`,
  appPaywallReceiptTitle: "Payment received",
  appPaywallReceiptAmount: "Amount",
  appPaywallReceiptOn: "Paid on",
  appPaywallReceiptRef: "Reference",
  appPaywallReceiptNextAccess: (date: string) => `App access active through ${date}.`,
  appPaywallReceiptContinue: "Continue to learning",
  monthlySuffix: " / month",
  attendancePresent: "Present",
  attendanceLate: "Late",
  attendanceAbsent: "Absent",
  attendanceExcused: "Excused",
  attendanceHalfDay: "Half day",
  loginHeroHeadline1: "Your learning,",
  loginHeroHeadline2: "all in one happy place.",
  loginHeroPointSeat: "Your seat, your hours",
  loginHeroPointReview: "A daily review queue built from your own gaps",
  loginWelcome: "Welcome back",
  loginSubtitle: (name: string) => `Sign in to ${name}`,
  loginEmail: "Email",
  loginPassword: "Password",
  loginPasswordPlaceholder: "Your password",
  loginTagline: (vertical: string): string =>
    vertical === "study_centre"
      ? "Your seat. Your focus. Your pace."
      : vertical === "coaching_institute"
        ? "Your classes. Your tests. Your progress."
        : "Your school day, all in one place.",
  loginSignIn: "Sign in",
  loginSigningIn: "Signing in…",
  loginInvalidCredentials: "That email or password didn't match. Try again.",
  loginFailed: "We couldn't sign you in. Try again shortly.",
  loginHelpNoun: (vertical: string): string =>
    vertical === "study_centre"
      ? "study centre"
      : vertical === "coaching_institute"
        ? "institute"
        : "school",
  loginVerticalLabel: (vertical: string): string =>
    vertical === "study_centre"
      ? "Study Centre"
      : vertical === "coaching_institute"
        ? "Coaching Institute"
        : "School",
  loginSwitchToLightMode: "Switch to light mode",
  loginSwitchToDarkMode: "Switch to dark mode",
  loginShowPassword: "Show password",
  loginHidePassword: "Hide password",
  loginTrouble: (noun: string) => `Trouble signing in? Ask your ${noun} for help.`,
  loginPoweredBy: "Powered by EduViaOS · Secure by design",
  loginMagicLinkCta: "Email me a sign-in link",
  loginMagicLinkSent: "If that email is on file, we sent a sign-in link. It expires in 15 minutes.",
  loginUsePassword: "Sign in with password instead",
  loginUseMagicLink: "Email me a sign-in link instead",
  loginMagicLinkPending: "Sending link…",
  loginMagicVerifyTitle: "Signing you in…",
  loginMagicVerifyFailed: "This sign-in link is invalid or has expired.",
  loginMagicVerifyError: "We couldn't verify this sign-in link. Request a new link and try again.",
  loginBackToSignIn: "Back to sign in",
  inviteExpiredError: "This invite link is no longer valid. Ask your centre to send a new one.",
  inviteAcceptFailed: "We couldn't activate your account. Try again shortly.",
  inviteChecking: "Checking your invite…",
  inviteLookupFailedTitle: "We couldn't check this invite",
  inviteLookupFailedBody: "The service is temporarily unavailable. Try the link again shortly.",
  inviteActivating: "Activating your account…",
  studyStreakDays: (n: number) => `${n} day${n === 1 ? "" : "s"} streak`,
  focusMinutesToday: (n: number) => `${n} min focus today`,
  studyStreakLabel: "Day streak",
  focusMinutesLabel: "Focus minutes today",
  // ZiggyChooser — first-run "meet your buddy" modal
  ziggyChooserEyebrow: "Meet Ziggy",
  ziggyChooserTitle: "Pick your study buddy",
  ziggyChooserBlurb: "Choose the Ziggy you like best. You can switch anytime from your profile.",
  ziggyChooseAria: (label: string) => `Choose ${label} Ziggy`,
  ziggyChoose: "Choose",
  ziggyBlurbPink: "Warm & playful",
  ziggyBlurbBlue: "Cool & focused",
  ziggySectionTitle: "Your Ziggy",
  ziggyLookGroupAria: "Ziggy look",
  ziggyVariantAria: (label: string) => `${label} Ziggy`,
  ziggySectionHint: "Ziggy grows up with you and keeps your institution's colours.",
  // Profile panel
  appearance: "Appearance",
  themeGroupAria: "Theme",
  themeLight: "Light",
  themeDark: "Dark",
  themeSystem: "System",
  appearanceHint: "Your institution sets the colours; you choose light or dark.",
  appLanguageGroupAria: "App content language",
  roleStudent: "Student",
  roleLearner: "Learner",
  roleGuardian: "Parent / Guardian",
  changeProfilePhoto: "Change profile photo",
  avatarUpdateFailed: "Could not update photo.",
  signOut: "Sign out",
  // Nav chrome
  notifications: "Notifications",
  notificationsWithUnread: (n: number) => `Notifications, ${n} unread`,
  askZiggy: "Ask Ziggy",
  primaryNavAria: "Primary",
  collapseSidebar: "Collapse sidebar",
  expandSidebar: "Expand sidebar",
  // Ask Ziggy sheet
  close: "Close",
  askZiggyGreeting: "Hi! I'm Ziggy. How can I help you today?",
  askZiggyPromptLead: "Try one of these to get started:",
  askZiggyPlaceholder: "Ask anything about your studies…",
  askZiggyComposerAria: "Message",
  askZiggySend: "Send",
  askZiggyCannedReply:
    "Great question! I can't answer that one yet. Ask your mentor or the team at your centre — they'll know.",
  askZiggyConversationAria: "Conversation with Ziggy",
} as const;

type ShellTable = {
  [K in keyof typeof SHELL_EN]: (typeof SHELL_EN)[K] extends (...args: infer A) => infer R
    ? (...args: A) => R
    : string;
};

const SHELL_HI: ShellTable = {
  navHome: "होम",
  navSeat: "सीट",
  navLearn: "सीखें",
  navGroups: "ग्रुप",
  navSupport: "सहायता",
  navProfile: "प्रोफ़ाइल",
  greetingMorning: "सुप्रभात",
  greetingAfternoon: "नमस्ते",
  greetingEvening: "शुभ संध्या",
  heroTagline: "आज आपकी सबसे बड़ी ताकत है — एकाग्रता।",
  heroCta: "शुरू करें",
  viewAll: "सभी देखें",
  todayAtAGlance: "आज की झलक",
  weekActivityTitle: "पिछले 7 दिन",
  weekActivityEmpty: "पिछले 7 दिनों में पढ़ाई की कोई गतिविधि नहीं।",
  weekActivityUnavailable: "हाल की गतिविधि लोड नहीं हो सकी।",
  weekActivityLatestOnly: "रिकॉर्ड की नवीनतम गतिविधियाँ दिख रही हैं।",
  topicsDueToday: (n: number) => `आज ${n} विषय दोहराने हैं`,
  caughtUpToday: "आज सब पूरा",
  dueNow: "अभी बाकी",
  reviewedToday: "आज रिव्यू",
  inRotation: "रोटेशन में",
  yourSeat: "आपकी सीट",
  regular: "नियमित",
  checkingSeat: "सीट जाँच हो रही है…",
  oneMoment: "एक पल।",
  seatHeldToday: "आज के लिए सीट",
  seatAssigned: "सीट निर्धारित",
  noSeatYet: "अभी कोई सीट नहीं",
  sitAnywhere: "कहीं भी बैठें",
  sitAnywhereHint: "कोई तय डेस्क नहीं। पहुँचकर सीट टैब पर चेक इन करें।",
  noBookingYet: "अभी कोई सीट बुक नहीं",
  bookSeatHint: "सीट टैब पर तारीख और शिफ्ट चुनकर डेस्क बुक करें।",
  bookedAhead: "सीट बुक है",
  nextBooking: (day: string) => `अगली: ${day}`,
  checkedIn: "चेक-इन हो गया",
  notCheckedInYet: "अभी चेक-इन नहीं",
  dedicatedSeatNoToday: "आपकी नियमित सीट। आज कुछ बुक नहीं।",
  seatAssignHint: "आपका केंद्र आपकी सीट तय करेगा। तैयार होते ही यहाँ दिखेगी।",
  continueLearning: "पढ़ाई जारी रखें",
  homeLoadFailedTitle: "डैशबोर्ड लोड नहीं हुआ",
  homeLoadFailedBody: "आपका अध्ययन डेटा लोड नहीं हुआ। थोड़ी देर बाद फिर कोशिश करें।",
  studyStatsUnavailableTitle: "अध्ययन आँकड़े उपलब्ध नहीं हैं",
  studyStatsUnavailableBody: "आज के रिव्यू आँकड़े लोड नहीं हुए। आपकी पढ़ाई की प्रगति सुरक्षित है।",
  homeRetry: "फिर कोशिश करें",
  noticesTitle: "सूचनाएँ",
  noticesLoading: "सूचनाएँ लोड हो रही हैं…",
  noticesLoadFailedTitle: "सूचनाएँ लोड नहीं हुईं",
  noticesLoadFailedBody: "आपके अपडेट लाने में कुछ गड़बड़ हुई। थोड़ी देर बाद फिर कोशिश करें।",
  noticesEmpty: "अभी कोई सूचना नहीं — प्रगति रिपोर्ट और केंद्र अपडेट यहाँ दिखेंगे।",
  noticesUnread: (n: number) => `${n} नई`,
  noticesMarkAllRead: "सभी पढ़ी हुई करें",
  noticesMarkingAllRead: "हो रहा है…",
  noticesMarkAllFailed: "पढ़ी हुई नहीं हो पाईं। थोड़ी देर बाद फिर कोशिश करें।",
  noticesUnreadDot: "नई",
  noticesOpen: (title: string) => `${title} — पढ़ी हुई करें`,
  noticesListAria: "सूचनाएँ",
  moduleDisabledHomeTitle: "लर्निंग ऐप चालू नहीं",
  moduleDisabledHomeBody:
    "आपके स्टडी सेंटर ने अभी लर्निंग ऐप चालू नहीं किया। सेंटर एडमिन से पूछें।",
  setupFirstPlan: "पहली योजना बनाएँ",
  setupFirstPlanHint: "ज़िगी को बताएँ क्या पढ़ रहे हैं। सीखें टैब में दो मिनट।",
  openLearn: "सीखें खोलें",
  quickActions: "त्वरित कार्य",
  takeQuiz: "क्विज़ लें",
  practiceTest: "अभ्यास टेस्ट",
  logReading: "पढ़ाई लॉग करें",
  progress: "प्रगति",
  progressReport: "प्रगति रिपोर्ट",
  progressReportHint: "अपना प्रदर्शन देखें",
  newReportsBadge: (n: number) => `${n} नई जानकारी`,
  practiceTestHint: "मॉक टेस्ट का प्रयास करें",
  logReadingHint: "पढ़ाई का हिसाब रखें",
  takeQuizHint: "कमज़ोर विषय पहचानें",
  seatToday: "आज",
  nothingHeldToday: "आज कुछ बुक नहीं",
  nothingHeldHintAssigned: (label: string) =>
    `आपकी नियमित सीट ${label} है। डेस्क शिफ्ट बुक कर सकती है।`,
  nothingHeldHintUnassigned: "फ्रंट डेस्क से सीट बुक करवाएँ — यहाँ दिखेगी।",
  yourRegularSeat: "आपकी नियमित सीट",
  seatDetailsAtDesk: "विवरण डेस्क पर",
  zoneAtCentre: "आपका केंद्र",
  comingUp: "आगामी",
  attendance: "उपस्थिति",
  attendanceToday: "आज",
  attendanceNoneToday: "आज अभी उपस्थिति दर्ज नहीं। आते ही चेक-इन करें।",
  last30Days: "पिछले 30 दिन",
  daysMarked: "दिन दर्ज",
  minLate: (n: number) => `${n} मिनट देरी`,
  imHereCheckIn: "मैं यहाँ हूँ — चेक-इन",
  checkInFailed: "चेक-इन नहीं हुआ। फिर कोशिश करें या डेस्क पर पूछें।",
  cancelBooking: "रद्द करें",
  cancelTodayBooking: "आज की बुकिंग रद्द करें",
  cancelBookingOn: (day: string) => `${day} की बुकिंग रद्द करें`,
  cancelBookingPrompt: "यह बुकिंग रद्द करें? सीट दूसरों के लिए खुल जाएगी।",
  keepBooking: "रहने दें",
  confirmCancelBooking: "हाँ, रद्द करें",
  cancelBookingFailed: "रद्द नहीं हुआ। फिर कोशिश करें या डेस्क पर पूछें।",
  bookAnotherSeat: "एक और सीट बुक करें",
  notLinkedTitle: "अभी रोस्टर से लिंक नहीं",
  seatUnavailableTitle: "सीट विवरण उपलब्ध नहीं",
  notLinkedBody: "सीट दिखाने से पहले केंद्र को इस लॉगिन को आपके रिकॉर्ड से जोड़ना होगा।",
  seatLoadFailedBody: "अभी सीट लोड नहीं हुई। थोड़ी देर बाद फिर कोशिश करें।",
  whatAppearsHere: "यहाँ क्या दिखेगा",
  explainSeatTitle: "आपकी सीट और ज़ोन",
  explainSeatBody: "केंद्र ने जो सीट आपके लिए रखी है।",
  explainShiftTitle: "आपकी शिफ्ट",
  explainShiftBody: "दिन-प्रतिदिन सीट कब तक के लिए है।",
  explainDeskTitle: "डेस्क पर पूछें",
  explainDeskBody: "तब तक फ्रंट डेस्क पर विवरण मिलेगा।",
  assignedByCentre: "केंद्र द्वारा निर्धारित",
  inZone: (seat: string, zone: string) => `${zone} में ${seat}`,
  nothingHeldHintBookBelow: "नीचे तारीख और शिफ्ट चुनकर डेस्क बुक करें।",
  bookASeat: "सीट बुक करें",
  pickDate: "तारीख",
  pickShift: "शिफ्ट",
  pickZone: "ज़ोन",
  pickZoneOptional: "ज़ोन से फ़िल्टर",
  allZones: "सभी ज़ोन",
  slotMorning: "सुबह",
  slotAfternoon: "दोपहर",
  slotEvening: "शाम",
  slotNight: "रात",
  slotFullDay: "पूरा दिन",
  attendanceStat: (value: string, label: string) => `${value} — ${label}`,
  bookSeat: "यह सीट बुक करें",
  bookZone: "यह ज़ोन बुक करें",
  selected: "चुना",
  seatTaken: "भरी",
  seatAvailable: "खाली",
  yourSeatLabel: "आपकी सीट",
  loadingAvailability: "उपलब्धता देखी जा रही है…",
  availabilityLoadFailed: "उपलब्धता लोड नहीं हुई। थोड़ी देर बाद फिर कोशिश करें।",
  shiftsLoadFailed: "शिफ्ट लोड नहीं हुईं। थोड़ी देर बाद फिर कोशिश करें।",
  noShiftsOnDay: "इस दिन कोई शिफ्ट खुली नहीं है। दूसरी तारीख चुनें।",
  noZonesAvailable: "इस शिफ्ट के लिए कोई ज़ोन नहीं।",
  zoneSpotsLeft: (n: number, cap: number) => `${cap} में से ${n} जगह बाकी`,
  zoneFullNoWaitlist: "यह ज़ोन इस शिफ्ट के लिए भरा है।",
  zoneFullWaitlist: "भरा — वेटलिस्ट के लिए फ्रंट डेस्क पर पूछें।",
  bookingConfirmedTitle: "बुक हो गया",
  bookingConfirmedBody: (day: string, slot: string) => `${day} · ${slot}।`,
  bookingSeatPending: "यह सीट बुक हो रही है…",
  bookingZonePending: "यह ज़ोन बुक हो रहा है…",
  bookingFailedGeneric: "बुकिंग नहीं हुई। फिर कोशिश करें या डेस्क पर पूछें।",
  seatAlreadyTaken: "वह सीट अभी ली गई। दूसरी चुनें।",
  alreadyBookedThisDay: "उस दिन पहले से बुकिंग है। सीट बदलना चालू नहीं।",
  bookingNotAllowed: "आपका केंद्र ऐप से यह बुकिंग नहीं करता।",
  alreadyHavePermanentSeat: "आपकी स्थायी सीट पहले से है।",
  namedShift: "शिफ्ट",
  bookByTheHour: "घंटे के हिसाब से",
  customStart: "शुरुआत",
  customHours: "घंटे",
  customCharge: (amount: string) => `शुल्क ${amount}`,
  customOverlaps: "उन घंटों में यह सीट भरी है। दूसरी सीट या समय चुनें।",
  customDisabled: "केंद्र अभी कस्टम अवधि की बुकिंग नहीं ले रहा।",
  permanentRoomsNote: "स्थायी कमरे डेस्क पर आवंटित होते हैं, यहाँ बुक नहीं होते।",
  fixedRoomNotBookable: "इस कमरे की सीटें स्थायी हैं। डेस्क पर पूछें।",
  planTitle: "आपका सीट प्लान",
  planFixedSeat: (place: string) => `आपकी सीट: ${place}। यह हर दिन आपके लिए रखी है।`,
  planFirstCome: "किसी शिफ्ट के लिए कोई भी खाली सीट बुक करें।",
  planFixedHoursShifts: (shifts: string) => `आपके घंटे: ${shifts}। इन्हीं में सीट बुक करें।`,
  planFixedHoursWindow: (start: string, end: string) =>
    `आपके घंटे: रोज़ ${start} – ${end}। इन्हीं में सीट बुक करें।`,
  planFlexibleHours: "शुरू होने का समय और कितने घंटे चाहिए, चुनें।",
  planZoneOnly: (zone: string) => `आपका कमरा: ${zone}। उसमें कहीं भी बैठें।`,
  planNoSeat: "कहीं भी बैठें — पहुँचकर चेक इन करें",
  planValidUntil: (day: string) => `${day} तक`,
  walkInCheckIn: "मैं आ गया — चेक इन करें",
  walkInCheckedIn: "आज के लिए चेक इन हो गया",
  walkInCheckInFailed: "चेक इन नहीं हो पाया। फिर कोशिश करें या डेस्क पर पूछें।",
  outsidePlanHours: "यह समय आपके घंटों से बाहर है। इनके अंदर का समय चुनें।",
  zoneCapacityToday: (taken: number, cap: number) => `अभी ${cap} में से ${taken} सीटें भरी हैं`,
  floorMapTitle: "फ़्लोर मैप",
  floorMapZoomIn: "ज़ूम इन",
  floorMapZoomOut: "ज़ूम आउट",
  floorMapReset: "पूरा फ़्लोर",
  floorMapHint: "ज़ूम के लिए पिंच करें या बटन दबाएँ। खाली सीट चुनने के लिए टैप करें।",
  floorMapLive: "लाइव",
  floorMapPolling: "हर 15 सेकंड में अपडेट",
  floorSeatFree: "खाली",
  floorSeatTaken: "भरी",
  floorSeatYours: "आपकी",
  floorSeatSelected: "चुनी गई",
  floorSeatAria: (seat: string, state: string) => `सीट ${seat}, ${state}`,
  floorMapAria: (name: string, free: number, total: number) =>
    `${name}: ${total} में से ${free} सीटें खाली`,
  claimYourSeat: "अपनी डेस्क चुनें",
  claimYourSeatHint: "एक बार स्थायी सीट चुनें। उसके बाद केंद्र उसे आपके लिए रखेगा।",
  claimSeat: "यह डेस्क चुनें",
  claimFailed: "डेस्क नहीं मिली। दूसरी चुनें या डेस्क पर पूछें।",
  groupsTitle: "अध्ययन समूह",
  newGroup: "नया समूह",
  expandGroupList: "सूची खोलें",
  collapseGroupList: "सूची बंद करें",
  groupsLoadFailedTitle: "अध्ययन समूह लोड नहीं हुए",
  groupsLoadFailedBody: "आपके समूह लाने में कुछ गड़बड़ हुई। थोड़ी देर बाद फिर कोशिश करें।",
  noGroupsYet: "अभी कोई समूह नहीं",
  noGroupsHint: "सहपाठियों के साथ रिवाइज़ के लिए एक बनाएँ।",
  archivedGroups: "संग्रहित",
  archivedReadOnly: "संग्रहित। इतिहास पढ़ सकते हैं। केंद्र वापस खोले तब तक नए संदेश बंद हैं।",
  members: (count: number, max: number) => `${count}/${max} सदस्य`,
  chat: "चैट",
  chatWithGroup: (name: string) => `चैट खोलें — ${name}`,
  join: "शामिल हों",
  joinGroupNamed: (name: string) => `${name} में शामिल हों`,
  createGroupTitle: "अध्ययन समूह बनाएँ",
  createGroupBlurb: "आपके सेंटर के साथी इस समूह को खोज कर जुड़ सकते हैं।",
  groupName: "समूह का नाम",
  descriptionOptional: "विवरण (वैकल्पिक)",
  createGroup: "समूह बनाएँ",
  needPlanFirst: "समूह से पहले सीखें टैब में योजना बनाएँ।",
  groupNameMin: "नाम कम से कम 3 अक्षर का हो।",
  inviteJoinFailed: "इनवाइट से शामिल नहीं हो सके।",
  createGroupFailed: "समूह नहीं बन सका।",
  joinGroupFailed: "शामिल नहीं हो सके।",
  selectGroupChat: "चैट के लिए समूह चुनें",
  selectGroupChatHint: "सूची से चुनें या नया समूह बनाएँ।",
  appLanguage: "ऐप भाषा",
  appLanguageHint: "पोर्टल लेबल और क्विज़ भाषा — सभी डिवाइस पर सिंक।",
  languageDeviceOnly: "इस डिवाइस पर सेव हुआ। आपका सेंटर रोस्टर जोड़ते ही सिंक हो जाएगा।",
  languageSaveFailed: "आपकी भाषा सेव नहीं हो सकी। थोड़ी देर बाद कोशिश करें।",
  errorOffline: "सर्वर तक नहीं पहुँचे। कनेक्शन जाँचें और फिर कोशिश करें।",
  errorTooManyRequests: "बहुत बार कोशिश हुई — थोड़ी देर रुककर फिर करें।",
  errorServiceDown: "यह अभी उपलब्ध नहीं है। थोड़ी देर बाद कोशिश करें।",
  learnLanguage: "सीखें भाषा",
  learnLanguageHint: "सीखें टैब और क्विज़ भाषा — इस डिवाइस पर।",
  appPaywallTitle: "लर्नर ऐप",
  appPaywallBody: (centre: string) => `${centre} लर्निंग ऐप अलग सदस्यता के रूप में देता है।`,
  appPaywallLapsedTitle: "सदस्यता समाप्त",
  appPaywallLapsedBody: (centre: string, expiredOn: string) =>
    expiredOn
      ? `${centre} पर आपकी लर्नर ऐप पहुँच ${expiredOn} को समाप्त हुई। पढ़ाई जारी रखने के लिए नवीनीकरण करें।`
      : `${centre} पर आपकी लर्नर ऐप पहुँच समाप्त हो गई है। पढ़ाई जारी रखने के लिए नवीनीकरण करें।`,
  appPaywallPayNow: "अभी भुगतान करें",
  appPaywallPaying: "चेकआउट खोला जा रहा है…",
  appPaywallPayAtCentre:
    "केंद्र के फ्रंट डेस्क पर भुगतान करें, फिर केंद्र की टीम से अपनी पहुँच चालू करने के लिए कहें। अनुमति मिलने पर आपकी योजनाएँ, कोच और प्रगति टूल यहाँ खुलेंगे।",
  appPaywallPayAtCentreSecondary:
    "नकद पसंद है? फ्रंट डेस्क पर भुगतान करें और केंद्र की टीम से पहुँच चालू करने को कहें — परिणाम वही।",
  appPaywallPaymentFailed: "भुगतान पूरा नहीं हो सका। फिर कोशिश करें या केंद्र पर भुगतान करें।",
  appPaywallVerifyFailed: "भुगतान सत्यापित नहीं हो सका। राशि कटी हो तो केंद्र से संपर्क करें।",
  appPaywallRenewalActive: (date: string) => `पहुँच सक्रिय — ${date} तक नवीनीकरण।`,
  appPaywallLapsedDaysAgo: (days: number) =>
    days <= 0
      ? "आज ही समाप्त हुई।"
      : days === 1
        ? "1 दिन पहले समाप्त हुई।"
        : `${days} दिन पहले समाप्त हुई।`,
  appPaywallReceiptTitle: "भुगतान प्राप्त हुआ",
  appPaywallReceiptAmount: "राशि",
  appPaywallReceiptOn: "भुगतान दिनांक",
  appPaywallReceiptRef: "संदर्भ",
  appPaywallReceiptNextAccess: (date: string) => `ऐप पहुँच ${date} तक सक्रिय है।`,
  appPaywallReceiptContinue: "सीखना जारी रखें",
  monthlySuffix: " / महीना",
  attendancePresent: "उपस्थित",
  attendanceLate: "देर",
  attendanceAbsent: "अनुपस्थित",
  attendanceExcused: "छूट",
  attendanceHalfDay: "आधा दिन",
  loginHeroHeadline1: "आपकी पढ़ाई,",
  loginHeroHeadline2: "एक ही जगह।",
  loginHeroPointSeat: "आपकी सीट, आपके घंटे",
  loginHeroPointReview: "आपकी कमज़ोरियों से बना दैनिक रिव्यू",
  loginWelcome: "वापसी पर स्वागत",
  loginSubtitle: (name: string) => `${name} में साइन इन करें`,
  loginEmail: "ईमेल",
  loginPassword: "पासवर्ड",
  loginPasswordPlaceholder: "आपका पासवर्ड",
  loginTagline: (vertical: string) =>
    vertical === "study_centre"
      ? "आपकी सीट। आपका फोकस। आपकी गति।"
      : vertical === "coaching_institute"
        ? "आपकी कक्षाएँ। आपके टेस्ट। आपकी प्रगति।"
        : "आपका स्कूल दिवस, एक ही जगह।",
  loginSignIn: "साइन इन",
  loginSigningIn: "साइन इन हो रहा है…",
  loginInvalidCredentials: "ईमेल या पासवर्ड मेल नहीं खाता। फिर कोशिश करें।",
  loginFailed: "साइन इन नहीं हो सका। थोड़ी देर बाद फिर कोशिश करें।",
  loginHelpNoun: (vertical: string) =>
    vertical === "study_centre"
      ? "अध्ययन केंद्र"
      : vertical === "coaching_institute"
        ? "कोचिंग संस्थान"
        : "स्कूल",
  loginVerticalLabel: (vertical: string) =>
    vertical === "study_centre"
      ? "अध्ययन केंद्र"
      : vertical === "coaching_institute"
        ? "कोचिंग संस्थान"
        : "स्कूल",
  loginSwitchToLightMode: "लाइट मोड पर जाएँ",
  loginSwitchToDarkMode: "डार्क मोड पर जाएँ",
  loginShowPassword: "पासवर्ड दिखाएँ",
  loginHidePassword: "पासवर्ड छिपाएँ",
  loginTrouble: (noun: string) => `समस्या? अपने ${noun} से मदद लें।`,
  loginPoweredBy: "EduViaOS · सुरक्षित",
  loginMagicLinkCta: "साइन-इन लिंक ईमेल करें",
  loginMagicLinkSent: "अगर ईमेल रजिस्टर्ड है, तो लिंक भेज दिया गया है। 15 मिनट में समाप्त होगा।",
  loginUsePassword: "पासवर्ड से साइन इन करें",
  loginUseMagicLink: "साइन-इन लिंक ईमेल करें",
  loginMagicLinkPending: "लिंक भेजा जा रहा है…",
  loginMagicVerifyTitle: "साइन इन हो रहा है…",
  loginMagicVerifyFailed: "यह लिंक अमान्य है या समाप्त हो गया है।",
  loginMagicVerifyError: "साइन-इन लिंक जाँचा नहीं जा सका। नया लिंक माँगकर फिर कोशिश करें।",
  loginBackToSignIn: "साइन इन पर वापस",
  inviteExpiredError: "यह आमंत्रण लिंक अब मान्य नहीं है। अपने केंद्र से नया लिंक माँगें।",
  inviteAcceptFailed: "खाता सक्रिय नहीं हो सका। थोड़ी देर बाद फिर कोशिश करें।",
  inviteChecking: "आमंत्रण जाँचा जा रहा है…",
  inviteLookupFailedTitle: "आमंत्रण जाँचा नहीं जा सका",
  inviteLookupFailedBody: "सेवा अभी उपलब्ध नहीं है। थोड़ी देर बाद लिंक फिर खोलें।",
  inviteActivating: "खाता सक्रिय हो रहा है…",
  studyStreakDays: (n: number) => `${n} दिन${n === 1 ? "" : ""} की लकीर`,
  focusMinutesToday: (n: number) => `आज ${n} मिनट फोकस`,
  studyStreakLabel: "दिन की लकीर",
  focusMinutesLabel: "आज के फोकस मिनट",
  ziggyChooserEyebrow: "जिग्गी से मिलें",
  ziggyChooserTitle: "अपना स्टडी बडी चुनें",
  ziggyChooserBlurb: "जो जिग्गी पसंद हो चुनें। प्रोफ़ाइल से कभी भी बदल सकते हैं।",
  ziggyChooseAria: (label: string) => `${label} जिग्गी चुनें`,
  ziggyChoose: "चुनें",
  ziggyBlurbPink: "गर्मजोशी और मस्ती",
  ziggyBlurbBlue: "शांत और एकाग्र",
  ziggySectionTitle: "आपका जिग्गी",
  ziggyLookGroupAria: "जिग्गी का रूप",
  ziggyVariantAria: (label: string) => `${label} जिग्गी`,
  ziggySectionHint: "जिग्गी आपके साथ बड़ा होता है और आपके संस्थान के रंग रखता है।",
  appearance: "दिखावट",
  themeGroupAria: "थीम",
  themeLight: "लाइट",
  themeDark: "डार्क",
  themeSystem: "सिस्टम",
  appearanceHint: "रंग आपका संस्थान तय करता है; आप लाइट या डार्क चुनें।",
  appLanguageGroupAria: "ऐप की भाषा",
  roleStudent: "छात्र",
  roleLearner: "शिक्षार्थी",
  roleGuardian: "अभिभावक",
  changeProfilePhoto: "प्रोफ़ाइल फ़ोटो बदलें",
  avatarUpdateFailed: "फ़ोटो अपडेट नहीं हो सकी।",
  signOut: "साइन आउट",
  notifications: "सूचनाएँ",
  notificationsWithUnread: (n: number) => `सूचनाएँ, ${n} नई`,
  askZiggy: "जिग्गी से पूछें",
  primaryNavAria: "मुख्य",
  collapseSidebar: "साइडबार छोटा करें",
  expandSidebar: "साइडबार बड़ा करें",
  close: "बंद करें",
  askZiggyGreeting: "नमस्ते! मैं जिग्गी हूँ। आज मैं आपकी क्या मदद करूँ?",
  askZiggyPromptLead: "शुरू करने के लिए इनमें से कोई चुनें:",
  askZiggyPlaceholder: "अपनी पढ़ाई से जुड़ा कुछ भी पूछें…",
  askZiggyComposerAria: "संदेश",
  askZiggySend: "भेजें",
  askZiggyCannedReply:
    "बढ़िया सवाल! इसका जवाब मैं अभी नहीं दे सकता। अपने मेंटर या केंद्र की टीम से पूछें — उन्हें पता होगा।",
  askZiggyConversationAria: "जिग्गी से बातचीत",
};

export function portalShellStrings(locale: PortalLocale): ShellTable {
  return locale === "hi" ? SHELL_HI : SHELL_EN;
}

export function timeGreetingLocalized(locale: PortalLocale, now = new Date()): string {
  const hour = now.getHours();
  const s = portalShellStrings(locale);
  if (hour < 12) return s.greetingMorning;
  if (hour < 17) return s.greetingAfternoon;
  return s.greetingEvening;
}

export function attendanceStatusLocalized(locale: PortalLocale, status: string): string {
  const s = portalShellStrings(locale);
  switch (status) {
    case "present":
      return s.attendancePresent;
    case "late":
      return s.attendanceLate;
    case "absent":
      return s.attendanceAbsent;
    case "excused":
      return s.attendanceExcused;
    case "half_day":
      return s.attendanceHalfDay;
    default:
      return status;
  }
}

/**
 * Centres name their own shifts, so most slot keys are opaque strings we must
 * pass through untouched. The four the platform ships as defaults do have a
 * translation — returning `null` for anything else lets `slotLabel` keep its
 * existing pass-through behaviour instead of guessing.
 */
export function slotNameLocalized(locale: PortalLocale, slotKey: string): string | null {
  const s = portalShellStrings(locale);
  switch (slotKey) {
    case "morning":
      return s.slotMorning;
    case "afternoon":
      return s.slotAfternoon;
    case "evening":
      return s.slotEvening;
    case "night":
      return s.slotNight;
    case "full-day":
    case "full_day":
      return s.slotFullDay;
    default:
      return null;
  }
}
